import api from '@/api/index';

const namespaced = true;

const state = {
  tugBarges: [],
  allTugBarges: [],
  tugBargesDropdown: [],
  contractDropdown: [],
  agenciesDropdown: [],
  tugBargesFiles: {},
  securityVendors: [],
  tugBargesPerformanceDetails: [],
  tugBargesPerformance: [],
  tugBargesPerformanceChart: [],
  tugBargesPerformanceFiles: {}
};

const getters = {
  tugBarges: state => state.tugBarges,
  allTugBarges: state => state.allTugBarges,
  tugBargesDropdown: state => state.tugBargesDropdown,
  contractDropdown: state => state.contractDropdown,
  agenciesDropdown: state => state.agenciesDropdown,
  tugBargesFiles: state => state.tugBargesFiles,
  securityVendors: state => state.securityVendors,
  tugBargesPerformanceDetails: state => state.tugBargesPerformanceDetails,
  tugBargesPerformance: state => state.tugBargesPerformance,
  tugBargesPerformanceChart: state => state.tugBargesPerformanceChart,
  tugBargesPerformanceFiles: state => state.tugBargesPerformanceFiles
};

const mutations = {
  SET_TUG_BARGES: (state, payload) => {
    state.tugBarges = payload;
  },
  SET_ALL_TUG_BARGES: (state, payload) => {
    state.allTugBarges = payload;
  },
  SET_TUG_BARGES_DROPDOWN: (state, payload) => {
    state.tugBargesDropdown = payload;
  },
  SET_CONTRACT_DROPDOWN: (state, payload) => {
    state.contractDropdown = payload;
  },
  SET_AGENCIES_DROPDOWN: (state, payload) => {
    state.agenciesDropdown = payload;
  },
  SET_TUG_BARGE_FILES: (state, payload) => {
    state.tugBargesFiles = payload;
  },
  SET_SECURITY_VENDORS: (state, payload) => {
    state.securityVendors = payload;
  },
  SET_TUG_BARGES_PERFORMANCE_DETAILS: (state, payload) => {
    state.tugBargesPerformanceDetails = payload;
  },
  SET_TUG_BARGES_PERFORMANCE: (state, payload) => {
    state.tugBargesPerformance = payload;
  },
  SET_TUG_BARGES_PERFORMANCE_CHART: (state, payload) => {
    state.tugBargesPerformanceChart = payload;
  },
  SET_TUG_BARGES_PERFORMANCE_FILES: (state, payload) => {
    state.tugBargesPerformanceFiles = payload;
  }
};

const actions = {
  async fetchTugBarges({ commit, rootGetters }, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const params = {
        year: payload?.year,
        owner_name: payload?.owner_name,
        search: payload?.search || filterConfig.search,
        sort_dir: payload?.sort_dir || 'asc',
        sort_by: payload?.sort_by || 'owner_name',
        page: payload?.page || filterConfig.currentPage,
        limit: payload?.limit || filterConfig.pageSize,
        is_get_deleted: payload?.is_get_deleted || false,
        tug_name: payload?.tug_name,
        barge_name: payload?.barge_name
      }
      const { data } = await api.getTugBarges(params);

      if (data.error) {
        if (payload?.is_get_all) {
          commit('SET_ALL_TUG_BARGES', [])
        } else {
          commit('SET_TUG_BARGES', [])
          commit('Table/SET_TOTAL_DATA', 0, { root: true })
          commit('Table/SET_TOTAL_PAGE', 1, { root: true })
        }
        return
      }

      if (payload?.is_get_all) {
        commit('SET_ALL_TUG_BARGES', data.data)
      } else {
        commit('SET_TUG_BARGES', data.data)
        commit('Table/SET_TOTAL_DATA', data.total ?? 0, { root: true })
        commit('Table/SET_TOTAL_PAGE', data.pages, { root: true })
      }
      return data
    } catch (error) {
      throw error.data;
    }
  },

  async fetchTugBargesDropdown({ commit }, payload) {
    try {
      const params = {
        data: payload.data,
        owner_name: payload.owner_name,
        ...(payload.owner_id ? { owner_id: payload.owner_id} : null)
      };
      const { data } = await api.getTugBargesDropdown(params);
      if (data) {
        commit('SET_TUG_BARGES_DROPDOWN', data);
      }
      return data;
    } catch (error) {
      commit('SET_TUG_BARGES_DROPDOWN', []);
      throw error.data || error;
    }
  },

  async fetchContractDropdown({ commit }, payload) {
    try {
      const { data } = await api.getTugBargesContractDropdown(payload);
      commit('SET_CONTRACT_DROPDOWN', data || []);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async fetchAgencies({ commit }, payload) {
    try {
      const { data } = await api.getAgencyManagements(payload);

      if (data?.error) {
        commit('SET_AGENCIES_DROPDOWN', []);
        return;
      }

      commit('SET_AGENCIES_DROPDOWN', data || []);
      return data;
    } catch (error) {
      commit('SET_AGENCIES_DROPDOWN', []);
      throw error;
    }
  },

  async downloadTugBarges({ commit, rootGetters }, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const data = await api.downloadTugBarges({
        search: filterConfig.search,
        ...payload})
      if (data) {
        commit('SET_TUG_BARGE_FILES', data)
      }
    } catch (error) {
      throw error.data;
    }
  },
  resetTugBargeFiles({ commit }) {
    commit('SET_TUG_BARGE_FILES', {})
  },
  async createTugBarge(_, payload) {
    try {
      const { data } = await api.postTugBarge(payload);
      return data;
    } catch (error) {
      throw error.data;
    }
  },

  async updateTugBarge(_, { id, form }) {
    try {
      const { data } = await api.putTugBarge(id, form);
      return data;
    } catch (error) {
      throw error.data;
    }
  },
  async fetchSecurityVendors({ commit }, payload) {
    try {
      const { data } = await api.getSecurityVendors(payload);
      commit('SET_SECURITY_VENDORS', data || []);
      return data;
    } catch (error) {
      commit('SET_SECURITY_VENDORS', []);
      throw error.data;
    }
  },
  async fetchTugBargesPerformanceDetails({ commit }, payload) {
    try {
      const { data } = await api.getTugBargesPerformanceDetails(payload.id, { month_year: payload.month_year });
      if (data) {
        commit('SET_TUG_BARGES_PERFORMANCE_DETAILS', data);
      }
      return { data };
    } catch (error) {
      throw error.data || error;
    }
  },
  async fetchTugBargesPerformance({ commit }, payload) {
    try {
      const params = {
        month_year: payload?.month_year
      }
      const { data } = await api.getTugBargesPerformance(params);
      if (data?.error) {
        commit('SET_TUG_BARGES_PERFORMANCE', []);
        return;
      }
      commit('SET_TUG_BARGES_PERFORMANCE', data || []);
      return data;
    } catch (error) {
      commit('SET_TUG_BARGES_PERFORMANCE', []);
      throw error.data || error;
    }
  },
  async fetchTugBargesPerformanceChart({ commit, rootGetters }, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const params = {
        search: payload?.search || filterConfig.search,
        sort_dir: payload?.sort_dir || 'asc',
        sort_by: payload?.sort_by || 'owner_name',
        page: payload?.page || filterConfig.currentPage,
        limit: payload?.limit || filterConfig.pageSize,
        month_year: payload?.month_year
      }
      const { data } = await api.getTugBargesPerformanceChart(params);
      if (data) {
        commit('SET_TUG_BARGES_PERFORMANCE_CHART', data);
      }
      return { data };
    } catch (error) {
      commit('SET_TUG_BARGES_PERFORMANCE_CHART', []);
      throw error.data || error;
    }
  },
  async postTugBargesPerformance({ _ }, payload) {
    try {
      const { data } = await api.postTugBargesPerformance(payload);
      return data;
    } catch (error) {
      throw error.data || error;
    }
  },
  async downloadTugBargesPerformance({ commit, rootGetters }, payload) {
    try {
      const filterConfig = rootGetters['Table/defaultConfig']
      const params = {
        month_year: payload?.month_year
      }
      const data = await api.getTugBargesPerformanceReport(params)
      if (data) {
        commit('SET_TUG_BARGES_PERFORMANCE_FILES', data)
      }
    } catch (error) {
      throw error.data;
    }
  },
  resetTugBargesPerformanceFiles({ commit }) {
    commit('SET_TUG_BARGES_PERFORMANCE_FILES', {})
  },
  async deleteTugBarge({}, payload) {
    try {
      await api.deleteTugBarge(payload)
    } catch (err) {
      throw err
    }
  }
};

export default { namespaced, state, getters, mutations, actions }; 
